// In-app copy of INUA's legal and support pages. Terms of Use and Privacy Policy
// follow INUA_Terms_of_Use_2.docx and INUA_Privacy_Policy_v3_0_updated.docx
// (effective 13 October 2026, with the wording fixes agreed in chat); the other
// pages come from INUA_FOOTER_PAGES_.docx. They must stay identical to the
// public pages at /terms and /privacy (public/terms, public/privacy).
// To change wording, edit the text below — nothing else needs touching.
//
// Bump TERMS_VERSION whenever the Terms of Use or Privacy Policy change in a
// way people must agree to again: everyone is shown the "Review Our Terms"
// sheet once more the next time they sign in.

import { supabase } from "./supabaseClient";

export const TERMS_VERSION = "2026-10-13";

export type LegalDocId = "terms" | "privacy" | "about" | "contact" | "faq";

export type LegalBlock =
  | { t: "h"; text: string } // numbered section heading
  | { t: "sub"; text: string } // smaller sub-heading
  | { t: "p"; text: string }
  | { t: "c"; title: string; text: string } // numbered clause: bold title, then text
  | { t: "ul"; items: string[] }
  | { t: "faq"; q: string; a: string };

export interface LegalDoc {
  title: string;
  meta: string | null;
  blocks: LegalBlock[];
}

export const LEGAL_DOCS: Record<LegalDocId, LegalDoc> = {
  "terms": {
    "title": "Terms of Use",
    "meta": "Effective date: 13 October 2026. Last updated: 13 October 2026.",
    "blocks": [
      {
        "t": "p",
        "text": "PLEASE READ THESE TERMS CAREFULLY. BY CREATING AN ACCOUNT (INCLUDING BY SIGNING IN WITH GOOGLE), APPLYING AS A SELLER, OR USING THE INUA PLATFORM IN ANY WAY, INCLUDING AS A GUEST, YOU AGREE TO BE BOUND BY THESE TERMS AND BY THE INUA PRIVACY POLICY, WHICH IS INCORPORATED HERE BY REFERENCE. IF YOU DO NOT AGREE, YOU MUST NOT USE THE PLATFORM."
      },
      {
        "t": "h",
        "text": "1. What INUA Is and Is Not"
      },
      {
        "t": "c",
        "title": "1.1 Marketplace Model.",
        "text": "INUA is an online market place which connects students and young entrepreneurs selling products and services (\"Sellers\") to buyers (\"Buyers\"). Users are Sellers, Buyers and guests (together, \"Users\"). \"Platform\" means the INUA website, web-application, store pages and related services."
      },
      {
        "t": "c",
        "title": "1.2 Facilitator Only.",
        "text": "INUA is neither the seller or manufacturer or supplier of any of the Products listed on the Platform. INUA does not own, inspect, store, deliver or warrant any Product or Service and does not take part in any transaction between a Seller and a Buyer. INUA only provides the tools to find, confirm and communicate with each other. The Seller and the Buyer deal directly with each other on each transaction."
      },
      {
        "t": "c",
        "title": "1.3 Independent Sellers.",
        "text": "Sellers are independent contractors. They are not INUA employees, partners, agents or representatives. Nothing in these Terms shall constitute an employment, agency or partnership relationship."
      },
      {
        "t": "c",
        "title": "1.4 No Payments Through INUA.",
        "text": "INUA does not hold, collect, process or transfer any payments. Prices, payment methods and delivery are agreed between Buyer and Seller and payment is made outside the Platform. INUA does not see or store payment details, is not a party to any payment and accepts no liability for any payment, price, refund, delivery or loss arising out of a transaction between Users."
      },
      {
        "t": "c",
        "title": "1.5 No Guarantee of Outcome.",
        "text": "INUA does not warrant that any listing is genuine, accurate, lawful or available, that any User is honest, or that any transaction will be completed or satisfactory. Users interact with each other entirely at their own risk."
      },
      {
        "t": "h",
        "text": "2. Eligibility and Accounts"
      },
      {
        "t": "c",
        "title": "2.1 Age.",
        "text": "You must be at least 18 years of age to use the Platform or create an account. By registering you confirm you meet this requirement. If the User cannot confirm this, we may suspend or delete any account."
      },
      {
        "t": "c",
        "title": "2.2 Creating an Account.",
        "text": "Sign up with your email or by signing in with Google. You must provide accurate, current and complete information and keep it up to date. You may not register under someone else's identity, email address, phone number or Google account."
      },
      {
        "t": "c",
        "title": "2.3 Account Security.",
        "text": "You're responsible for maintaining the security of your login information and devices and for all activities that occur under your account. If you suspect any unauthorised use please notify us immediately at help@inuamarketplace.com."
      },
      {
        "t": "c",
        "title": "2.4 One Account.",
        "text": "Each person is allowed one account. You may not open additional accounts to work around a suspension, ban, rejection or blacklisting."
      },
      {
        "t": "h",
        "text": "3. Seller Application and Verification"
      },
      {
        "t": "c",
        "title": "3.1 Required application.",
        "text": "The Seller's listings will not go live until the Seller has submitted a complete application and INUA has approved it. Approval is at the sole discretion of INUA."
      },
      {
        "t": "c",
        "title": "3.2 Your Contributions.",
        "text": "You must give us your real name, phone number and location and upload samples of your own work so that we can verify they are authentic and have not been copied from the internet or from other people. You confirm that you are the original author of all submitted work."
      },
      {
        "t": "c",
        "title": "3.3 Registered Categories.",
        "text": "You register in a category when you apply. Your listed products and services must be within your registered category. You can't list outside your registered category. You must first get approval to sell in another category."
      },
      {
        "t": "c",
        "title": "3.4 Applications Not Accepted.",
        "text": "If INUA rejects your application, we will email you with instructions on how to fix it. Then you can resubmit your application with corrections within 24 to 48 hours of receiving that email. Being blocked is not a rejected application. Resubmission is not a guarantee of approval and a false or misleading resubmission will be treated as a breach of these Terms."
      },
      {
        "t": "c",
        "title": "3.5 Banned sellers cannot re-register.",
        "text": "A seller whose account has been banned and removed as per section 6 cannot use any other name or email address or Google account to register again on the Platform. The only people allowed to register again are those who had their application rejected as per section 3.4."
      },
      {
        "t": "c",
        "title": "3.6 Ongoing verification.",
        "text": "INUA has the right to request further proof of authenticity of the work listed on the site at any moment. If the seller does not respond in a timely manner, the listing or account may be suspended."
      },
      {
        "t": "h",
        "text": "4. Listings and Seller Obligations"
      },
      {
        "t": "c",
        "title": "4.1 Seller Accountability.",
        "text": "Sellers are fully responsible for their listings and must ensure their prices, descriptions, images, information about works' availability, and quality, delivery methods, customer communication and practices, and compliance with all applicable legislation, including licensing requirements and tax and consumer protection laws, are correct and true."
      },
      {
        "t": "c",
        "title": "4.2 True Listings.",
        "text": "Listings should be correct and true. Photos should be of sellers' own real products. Sellers shall not use the photos they did not take or create, or photos of someone else's work, including edited and stock photographs."
      },
      {
        "t": "c",
        "title": "4.3 Price in Listings.",
        "text": "The price shown in your listing is final. Do not provide different or misleading price or payment information in your listings or messages in a deceptive way."
      },
      {
        "t": "c",
        "title": "4.4 Contact Information.",
        "text": "Phone and WhatsApp numbers you provide must be real and your own so the Buyer is enabled to contact you. By including phone numbers in your listings, you agree that Buyers will use them to communicate with you."
      },
      {
        "t": "h",
        "text": "5. Prohibited Conduct"
      },
      {
        "t": "p",
        "text": "You cannot use the Platform for:"
      },
      {
        "t": "ul",
        "items": [
          "Offering, selling or marketing anything that's illegal under Kenyan law, which includes stolen, counterfeited or prohibited products and services.",
          "Adding the products and services that aren't included in your registered category.",
          "Publishing false, robbed, copied or altered work, or images that you did not create.",
          "Providing misleading information about yourself, your business, or your products or services.",
          "Avoiding or trying to avoid any verification, denying your account, getting banned, or blacklisted.",
          "Threatening, harming or putting someone in danger, or doing any kind of discrimination.",
          "Swindling or deceiving any User, for instance, receiving payment for goods or services that you're not going to do.",
          "Using the Platform for anything except real trade.",
          "Placing spam, stealing, or gathering information from the Platform.",
          "Interfering with the Platform, or trying to access it without permission.",
          "Encouraging or supporting others in performing the above actions."
        ]
      },
      {
        "t": "h",
        "text": "6. Enforcement: Suspension, Blacklisting and Bans"
      },
      {
        "t": "c",
        "title": "6.1 Our Rights.",
        "text": "If we reasonably suspect that you have violated these Terms, committed a crime or endangered any user or the Platform itself, we can, without prior notice and liability to you, take any action we deem appropriate, including but not limited to removal of content or listings, suspension or restriction of your account, and banning of your account."
      },
      {
        "t": "c",
        "title": "6.2 Counterfeit Properties.",
        "text": "When we discover that a Seller displays counterfeit properties, we will blacklist and suspend the Seller's account permanently and that Seller will never be able to register again."
      },
      {
        "t": "c",
        "title": "6.3 Record Keeping.",
        "text": "In order to enforce a ban and prevent a banned person from returning, we may retain certain information related to the banned account, which shall be limited to name, contact information, and the reasons for the ban, in accordance with the Privacy Policy and Data Protection Act, 2019, even after the account has been deleted."
      },
      {
        "t": "c",
        "title": "6.4 Involvement of Law Enforcement.",
        "text": "In cases where conduct is especially serious or severe, like fraud, theft, harassment, threats or violence, INUA might notify Kenyan law enforcement agencies of the situation, keep records related to the problem, and cooperate with their investigation."
      },
      {
        "t": "c",
        "title": "6.5 Review of Decisions.",
        "text": "If you believe an action against your account was a mistake, you may write to help@inuamarketplace.com. We will consider it in good faith but are not obliged to reverse any decision, and our decision is final."
      },
      {
        "t": "h",
        "text": "7. Safety, Reporting and Disputes"
      },
      {
        "t": "c",
        "title": "7.1 Your Safety.",
        "text": "When Users meet face to face, look for a busy place such as a coffee shop or a public place on a university campus. INUA does not check whether the meeting is safe. You are responsible for your safety. Make a decision at the meeting with caution, and if you feel in danger, leave and notify someone about the situation."
      },
      {
        "t": "c",
        "title": "7.2 Reporting.",
        "text": "Notify INUA about any fraudulent or abusive behavior by sending an email to trust@inuamarketplace.com as soon as you can and provide all the details about the situation with any evidence."
      },
      {
        "t": "c",
        "title": "7.3 Investigation.",
        "text": "Upon receiving the report, INUA may examine the chats between Users as well as other data from the Platform. Please note that this is only done upon receiving a report and does not happen regularly."
      },
      {
        "t": "c",
        "title": "7.4 Conflicts between Users.",
        "text": "INUA has no obligation to resolve disagreements between Buyers and Sellers or to provide any refunds or remedies. We will consider reported disagreements in good faith and may take actions against accounts found to have been dishonest or in error. Users should agree on the price, timeline, and requirements ahead of time and keep a record of their agreements."
      },
      {
        "t": "h",
        "text": "8. Charges"
      },
      {
        "t": "c",
        "title": "8.1 Currently Free.",
        "text": "The Platform is free to use as of the Effective Date. INUA does not charge Buyers or Sellers to set up accounts, list items, or communicate with each other."
      },
      {
        "t": "c",
        "title": "8.2 Possible Future Charges.",
        "text": "INUA may introduce charges of any kind to the Platform in the future. Any such additional charge will be introduced only after Users have received a notice from us via the Platform and/or email regarding the amount of the charge and when it starts. None of those charges will apply to you unless you choose to use the given service after the new charges are put in place. You will never be charged retroactively for any period before such a notice."
      },
      {
        "t": "h",
        "text": "9. Content and Intellectual Property"
      },
      {
        "t": "c",
        "title": "Your Content.",
        "text": "You will own the ownership of the photos, descriptions, and other content uploaded by you (\"Your Content\"). You confirm that you own the content or have the rights to use it and that it does not violate anyone's rights or laws."
      },
      {
        "t": "c",
        "title": "License to INUA.",
        "text": "You give INUA a limited, global, non-exclusive, and royalty-free license to keep, host, show, reproduce, and process Your Content only for the purposes concerning the Platform's operation, security, improvement, and promotion. The grant of the license will cease when the content is deleted from the Platform, except for the copies that are allowed under the Privacy Policy, and for the reasons of legal compliance or in the routine backups."
      },
      {
        "t": "c",
        "title": "INUA Property.",
        "text": "INUA and its name, logo, design, software and site belong to INUA. You should not copy, imitate, reproduce, decompile or use them in any other way without getting prior written authorization from us."
      },
      {
        "t": "c",
        "title": "Infringement Claims.",
        "text": "If you think that a particular content at the Platform violates your rights, please email help@inuamarketplace.com specifying all necessary details and evidence. We may remove the content and undertake actions against the account in question."
      },
      {
        "t": "h",
        "text": "10. Privacy"
      },
      {
        "t": "p",
        "text": "Our Privacy Policy clarifies how we handle personal data and is part of these Terms. You are required to comply with other users' personal data and use it only for the relevant transaction."
      },
      {
        "t": "h",
        "text": "11. Disclaimers and Limits of Liability"
      },
      {
        "t": "c",
        "title": "11.1 \"As Is\".",
        "text": "The Platform is provided on \"as is\" and \"as available\" bases. To the extent permitted by the law, INUA does not make any representations, neither express nor implied, concerning quality, safety, legality, truthfulness, availability, or suitability for specific purposes of the Platform."
      },
      {
        "t": "c",
        "title": "11.2 Availability.",
        "text": "INUA does not warrant that the Platform will operate constantly, without interference or outages and will always be safe. Access to the Platform may be affected by maintenance, upgrades, failures, or other circumstances beyond our control."
      },
      {
        "t": "c",
        "title": "11.3 No Liability.",
        "text": "INUA does not provide any insurance or coverage for accidents, injuries, loss, theft, damage or non-delivery happening or related to the use of the Platform."
      },
      {
        "t": "c",
        "title": "11.4 Exclusions.",
        "text": "To the maximum extent permitted by law, INUA disclaims all responsibility for:"
      },
      {
        "t": "ul",
        "items": [
          "The quality, safety, legality or correctness of any listings or User Content.",
          "Any losses, damages or conflicts deriving from any interaction, meeting or transaction among Users, including any payments done outside of the Platform.",
          "The actions of any User or third party or lack of such actions.",
          "Any loss derived from your failings to observe any safety recommendation or protect your account.",
          "Any losses of an indirect, incidental, consequential or special nature, or losses of profit, revenues, data or opportunities.",
          "Suspension, removal or banning of an account or Content under these Terms.",
          "Any third party services we are cooperating with or providing you with alongside the Platform, like Google, Supabase, Netlify, WhatsApp, and many more."
        ]
      },
      {
        "t": "c",
        "title": "11.5 Limit.",
        "text": "Even if INUA is found liable for some reason notwithstanding the above, its total liability for all claims connected to the Platform shall not exceed any amount you have paid to INUA during the preceding 12 months, which, while the Platform is free of charge, means nothing or KES 1,000, whichever is more."
      },
      {
        "t": "c",
        "title": "11.6 What Is Not Excluded.",
        "text": "Nothing in these Terms excludes or limits liability that legally cannot be excluded or limited according to law."
      },
      {
        "t": "h",
        "text": "12. Indemnity"
      },
      {
        "t": "p",
        "text": "You agree to compensate and shield INUA and its owners, directors, officers and employees against claims, losses, damages, costs (including reasonable attorney fees) resulting from the use of the Platform, your Content, your listings, your breach of these Terms and/or law and your relationship with other users."
      },
      {
        "t": "h",
        "text": "13. Suspension/Termination"
      },
      {
        "t": "c",
        "title": "13.1 By You.",
        "text": "You may stop using the Platform and delete your account any time. Some provisions that, by their nature, are expected to remain in force (the ones specified in clauses 3.5, 6, 9, 11, 12, 15 and 16) remain valid after the termination. Deletion of your account doesn't eliminate suspension/blacklist."
      },
      {
        "t": "c",
        "title": "13.2 By INUA.",
        "text": "We may suspend or terminate your access whenever it is provided for in clause 6 or in case we terminate or change the Platform. We will strive to provide you with a reason for acting in this manner if it is possible."
      },
      {
        "t": "h",
        "text": "14. Changes to These Terms"
      },
      {
        "t": "p",
        "text": "We may make changes to these Terms occasionally to adjust for updates on the Platform and on any changes in our prices and/or laws. Updates will be made available on the website with the date of their coming into force. For significant changes, notification will be sent by email or through the Platform. If you continue to use the Platform after such changes take effect, this will mean that you agree with the updated Terms of Usage. If you do not agree with the Terms of Use, then please stop using the services of the Platform."
      },
      {
        "t": "h",
        "text": "15. Jurisdiction and Disputes"
      },
      {
        "t": "p",
        "text": "These Terms are regulated by the basic laws of the Republic of Kenya. If any controversy occurs, the parties will initially try to settle it amicably through the email help@inuamarketplace.com. If the parties do not manage to resolve their issues in this case, the dispute will be sent to the relevant courts of Kenya to be solved there, however, this does not prevent the User from contacting the Office of the Commissioner for Data Protection."
      },
      {
        "t": "h",
        "text": "16. General clauses"
      },
      {
        "t": "c",
        "title": "16.1 Force Majeure.",
        "text": "INUA shall not be liable for delays and disruptions occurring as a result of force majeure situations."
      },
      {
        "t": "c",
        "title": "16.2 Severability.",
        "text": "If any part of these Terms is ruled to be invalid or unenforceable, it will be rewritten so that it has the smallest possible effect on the rest of the Terms or will be eliminated. The rest of the Terms remain in force."
      },
      {
        "t": "c",
        "title": "16.3 Entire Agreement.",
        "text": "These Terms alongside the Privacy Policy represent all the agreements between you and INUA concerning the Platform and cancel all earlier agreements."
      },
      {
        "t": "c",
        "title": "16.4 No Waiver.",
        "text": "Failing to enforce any right does not constitute a waiver of said right."
      },
      {
        "t": "c",
        "title": "16.5 Assignment.",
        "text": "You cannot assign your rights and obligations under these Terms. INUA can do that."
      },
      {
        "t": "c",
        "title": "16.6 Language.",
        "text": "These Terms are issued in English. In case it is translated, the English text takes precedence."
      },
      {
        "t": "h",
        "text": "17. Contact"
      },
      {
        "t": "p",
        "text": "If you have any questions regarding any provision of these Terms: help@inuamarketplace.com"
      },
      {
        "t": "p",
        "text": "If you wish to report fraud, harassment or any safety-related issue: trust@inuamarketplace.com"
      },
      {
        "t": "p",
        "text": "© 2026 INUA. All Rights Reserved."
      }
    ]
  },
  "privacy": {
    "title": "Privacy Policy",
    "meta": "Effective date: 13 October 2026. Last updated: 13 October 2026.",
    "blocks": [
      {
        "t": "p",
        "text": "ENSURE YOU GO THROUGH THE PRIVACY POLICY WITH UTMOST CARE. BY EITHER SIGNING UP ON THE INUA PLATFORM, LOGGING IN WITH GOOGLE, SELLING OR BUYING ANYTHING FROM INUA, OR ANY OTHER USAGE OF THE PLATFORM, YOU DECLARE THAT YOU ARE AWARE OF THIS POLICY AND HAVE FULLY UNDERSTOOD ITS CONTENTS. IF YOU DISAGREE WITH ANY OF THE TERMS HEREIN, YOU SHOULD REFRAIN FROM USING THE PLATFORM."
      },
      {
        "t": "p",
        "text": "This privacy policy describes how INUA collects, utilizes, shares, stores and safeguards personal data in compliance with the Data Protection Act, 2019 of Kenya and its regulations and the guidelines issued by the Office of the Data Protection Commissioner. Wherever there is reliance on your consent, it must be obtained in clear manner and you should be entitled to withdraw it when necessary (refer to point 12)."
      },
      {
        "t": "h",
        "text": "1. Who we are and what INUA is about"
      },
      {
        "t": "c",
        "title": "1.1 Marketplace model.",
        "text": "INUA is an online platform that brings together university students and graduate entrepreneurs (Sellers) with buyers from all over the country (Buyers). The buyers, sellers and visitors are collectively called \"Users\"."
      },
      {
        "t": "c",
        "title": "1.2 INUA serves only as a facilitator.",
        "text": "Our role is limited to providing a platform that allows Users to connect with one another. Sellers work independently and are not employees, partners, or agents of INUA. INUA is in no way involved in transactions made between Buyers and Sellers."
      },
      {
        "t": "c",
        "title": "1.3 Data Controller.",
        "text": "INUA is responsible for the personal data under consideration. For data protection inquiries, please contact us at trust@inuamarketplace.com."
      },
      {
        "t": "c",
        "title": "1.4 Definitions.",
        "text": "\"Personal Data\" refers to any information that relates to an identified or identifiable individual. \"Sensitive Personal data\" has the meaning assigned in the Act and covers data revealing race, health condition, ethnic or social origin, beliefs, genetic or biometric data, property-related information, marital or family status and sex or sexual preferences. \"Data subjects\" mean individuals whose personal data is involved. \"Processing\" means any processing of personal data, including collecting, storage, use, transfer and deletion. \"Platform\" means INUA website, web application, store's pages and any website-related activity."
      },
      {
        "t": "c",
        "title": "1.5 No Transactions via INUA.",
        "text": "INUA does not handle transactions in any way. Buyers and Sellers agree on prices, payments and deliveries between themselves and make their payments independently of INUA. INUA does not take part in the transactions in any way. INUA does not see or store any information related to payments and transactions, such as payment methods or details. INUA has no connection to any disputes that may arise between Users and shall bear no responsibility for any losses incurred in connection with any payments made outside the Platform. INUA can, but is not obligated to, check the information provided by Users and take any actions regarding accounts or content. Any information shared by Users pertains to transactions and payments undertaken at Users' own risk."
      },
      {
        "t": "c",
        "title": "1.6 Coverage of the policy.",
        "text": "This policy is applicable to all Users, including Guests and covers the entirety of data processed through the Platform. It shall not apply to third-party services we do not own or control."
      },
      {
        "t": "h",
        "text": "2. Eligibility and Account Integrity"
      },
      {
        "t": "c",
        "title": "2.1 Minimum Age.",
        "text": "The Platform is meant for people who are of adult age, that is, 18 years and above. By signing up you acknowledge that you are 18 years old or above and we may suspend or terminate the account if this cannot be confirmed."
      },
      {
        "t": "c",
        "title": "2.2 Accurate Information.",
        "text": "It is important that all information provided by you is true, accurate, up to date and complete. You cannot sign up under someone else's name, email address or phone number, or provide fake documents or information."
      },
      {
        "t": "c",
        "title": "2.3 Security of the Account.",
        "text": "You are solely responsible for all activity on your account and making your identifier safe. Please contact us at trust@inuamarketplace.com immediately, if you suspect that your account has been accessed by an unauthorized person."
      },
      {
        "t": "c",
        "title": "2.4 Fraud and Misrepresentation.",
        "text": "Giving incorrect identity, verification or listing information is a grave issue. We have the right to immediately suspend or terminate an account, remove related content, keep suitable records, and also inform appropriate authorities in Kenya."
      },
      {
        "t": "h",
        "text": "3. What Personal Data We Gather"
      },
      {
        "t": "sub",
        "text": "3.1 From Buyers"
      },
      {
        "t": "ul",
        "items": [
          "Email (which is required for setting up your account and communication purposes).",
          "Name and profile photo (if signing up via Google).",
          "Historical data associated with your browsing and purchases, which will be used solely for ensuring Platform safety, avoiding fraudulent acts, problems resolution.",
          "Messages and other data submitted via the Platform."
        ]
      },
      {
        "t": "sub",
        "text": "3.2 From Sellers"
      },
      {
        "t": "ul",
        "items": [
          "Full name.",
          "Contact number (it may also be WhatsApp number).",
          "Email address.",
          "Google account name and profile photo (only if you signed up using Google).",
          "University or institution you are associated with.",
          "City of residence (general location is enough; no need to put your street).",
          "Examples of your work that you will be asked to provide during the sign-up stage (to ensure that they were created by you) and other information about your listing (store details, products descriptions, prices, and images).",
          "History of orders and transactions done through your listings.",
          "Messages and content submitted via the Platform."
        ]
      },
      {
        "t": "c",
        "title": "3.3 Automatically Collected Information.",
        "text": "Basic technical information which includes, but is not limited to, device type of computer, browser type, general usage data, error logs and security logs, is used only to maintain the platform in a good working condition. Guests who use the platform without registering account are only able to provide this technical data."
      },
      {
        "t": "c",
        "title": "3.4 Cookies and Similar Technologies.",
        "text": "Cookies are small files that a website saves on your computer via your browser. Local storage is another similar feature that is available in the browser. It should be stressed that we do not use cookies or trackers for advertising aims. We do not use third parties' analytic and advertising means for tracking our visitors. The only tools that keep you signed in to the platform are the platform itself and the authentication provider (Supabase). Hosting providers (Netlify), as well as other third party providers, may automatically obtain some technical information once you land on the website."
      },
      {
        "t": "c",
        "title": "3.5 Google Sign-In and Google User Data.",
        "text": "When you utilize \"Continue with Google,\" Google provides us with your name, your e-mail address, and your profile photo. We do not require or obtain other Google data like your contacts, your e-mail, your calendar, and your files."
      },
      {
        "t": "p",
        "text": "The purpose of this data is to create and manage the account for INUA, the authentication into the Platform, and sending messages related to the service. Your name and photo may also appear on your profile or store page as outlined in the section 6. Google information is not used for advertising purposes, nor do we sell the data or forward it to the data brokers."
      },
      {
        "t": "p",
        "text": "We store and share Google information via our database and authentication service provider (Supabase). Their servers may be placed outside Kenya (see section 10). The information is transmitted only to people authorized by us or as required by the law."
      },
      {
        "t": "c",
        "title": "Retention and deletion:",
        "text": "we retain it for the duration of your account's existence and delete it or anonymise it within 30 days of a request for account deletion, as set out in section 11.2. You can also cancel INUA's permission at any point from your Google account settings."
      },
      {
        "t": "p",
        "text": "The use of data received from Google in accordance with the Google API Services User Data Policy, subject to the Limited Use requirements. Signing in via Google is optional, and Google is responsible for the data it possesses about you."
      },
      {
        "t": "c",
        "title": "3.6 Giving Data is an Optional Procedure.",
        "text": "You may choose whether to give us your personal information. Only part of the information is essential for providing the service. For instance, we can't create your account without an e-mail address; Sellers cannot make their listings available until they receive an account verification. If you choose not to provide this information, it might hinder your access to some features."
      },
      {
        "t": "c",
        "title": "3.7 Work Samples and Sensitive Information.",
        "text": "We do not require any identification documents (like any photos of national ID cards or identities) from sellers to ensure their identity verification. Instead, the only verification that we need from our sellers is their work samples, which we will use to confirm that the seller is not committing any theft of any kinds. When you upload your work sample, you hereby confirm that it is your own work and you have the right to use that work sample. All work samples will only be used for the verification of its authenticity and to provide safety for the platform. You must avoid sharing any sensitive information (regarding any health matters, politics, ethnicity, or religion) in your listings, profiles, work samples, or messages. Failing to provide such work samples will result in failure to activate your listings on the site. If you want to withdraw your consent, make sure to contact us and know that all features requiring work samples verification will cease functioning."
      },
      {
        "t": "c",
        "title": "3.8 Data From Other Users.",
        "text": "If you provide us with personal details about anyone else (for instance, someone else shown in an image), you affirm that you are authorized to do so and that the person is informed."
      },
      {
        "t": "c",
        "title": "3.9 The method of obtaining information.",
        "text": "The data comes from you personally (registration, approval of the seller, posts, orders, messages, and appeals for services), there is also automatic collection (technical data as described in parts 3.3 and 3.4), and information from third parties which you have selected to use (e.g. Google if you log in using Google)."
      },
      {
        "t": "h",
        "text": "4. The reason why we process your data (the basis for processing)"
      },
      {
        "t": "p",
        "text": "Personal information will be processed solely based on the grounds recognized by the Act:"
      },
      {
        "t": "ul",
        "items": [
          "Consent: when you create an account, sign in using Google, and/or accept this policy. It is possible to withdraw your consent at any time.",
          "Contract: in order to provide our core service, which is connecting buyers and sellers.",
          "Legitimate interest: to ensure the security of the platform, protect against fraud and abuse, check if the samples of seller's work are authentic, and to investigate complaints and issues regarding safety.",
          "Legal obligation: if it is required by Kenyan law, a court decision or an authorized request."
        ]
      },
      {
        "t": "h",
        "text": "5. How We Use Your Data"
      },
      {
        "t": "p",
        "text": "We use personal information for:"
      },
      {
        "t": "ul",
        "items": [
          "Opening, managing, and securing accounts.",
          "Reviewing seller work samples uploaded during registration in order to confirm that they are legitimate before listings can go live.",
          "Displaying sellers' stores and listings and facilitating Buyer-Seller communication.",
          "Keeping records of orders to resolve disputes and protect the Platform.",
          "Investigating and taking action in cases of fraud, abuse, harassment, or other violations.",
          "Detecting and preventing unauthorized access, scraping, spam, and misuse.",
          "Maintaining, troubleshooting, and improving the usability and reliability of the Platform.",
          "Sending service messages regarding your account, orders, or the Platform.",
          "Working within the constraints set by applicable laws and enforcing our rights."
        ]
      },
      {
        "t": "p",
        "text": "We don't use personal data for advertising and don't sell or make decisions affecting you based on automated processing without human review. Automated tools might be used to flag suspicious activity for review."
      },
      {
        "t": "c",
        "title": "Communications.",
        "text": "We send only communications related to services such as account-related messages, notifications, order messages, and security-related notices. We do not send any promotional messages unless we have a valid legal basis for sending material and if your consent is required, as appropriate. You can unsubscribe from non-essential messages at any time, and we do not use any sensitive personal information for marketing purposes."
      },
      {
        "t": "h",
        "text": "6. What Is Visible to Other Users"
      },
      {
        "t": "c",
        "title": "6.1 Public Information.",
        "text": "The pages and the listings of Seller stores can be accessed through shared links, which is something that Sellers can circulate. The contents of the store page or listing (like the Seller name, information about the products, prices, images, name of the university or other organization, city of place) can be accessible to every person who has a link, even those who do not have an account. Some materials in the store may also be available only for logged-in users."
      },
      {
        "t": "c",
        "title": "6.2 Contact Information.",
        "text": "The contact information of the Seller (like the phone number or WhatsApp number) is provided to Buyers so they can proceed with the transaction. By publishing that data, the seller agrees to share these details. The information of Buyers is shared with Sellers only when it is needed for the execution of a particular order or request."
      },
      {
        "t": "c",
        "title": "6.3 Your Responsibility.",
        "text": "You shall not post anything in your profile, listing, description or message that you do not want to be seen by the aforementioned people. The data visible for other Users may be copied or passed outside your control."
      },
      {
        "t": "c",
        "title": "6.4 Store Links.",
        "text": "Store links are formed through a random store code. Account identifiers won't appear in store links. Any individual who gets a store link can use it. It's your job to choose who gets it."
      },
      {
        "t": "h",
        "text": "7. Messages and When We Access Them"
      },
      {
        "t": "c",
        "title": "7.1 Limited Access.",
        "text": "INUA may read messages exchanged between Buyers-Sellers only in the following circumstances:"
      },
      {
        "t": "ul",
        "items": [
          "A User reports the other for fraud, harassment, assault or safety/security concern.",
          "Access is required to solve the dispute regarding payment, delivery or transaction.",
          "We have reasons to believe there is a violation of regulations, illegal activity or potential harm to a person or Platform.",
          "A court/law enforcement body or some other competent authority in Kenya requires it."
        ]
      },
      {
        "t": "c",
        "title": "7.2 Not Routine.",
        "text": "We do not check or read private messages and do not use them for tracking purposes or advertising campaign. Access is only provided for the particular conversation."
      },
      {
        "t": "c",
        "title": "7.3 Your Acceptance.",
        "text": "By using the Platform, you agree with the limited access."
      },
      {
        "t": "h",
        "text": "8. Data Sharing and Disclosure"
      },
      {
        "t": "sub",
        "text": "8.1 We Never Sell Your Personal Data."
      },
      {
        "t": "sub",
        "text": "8.2 We Share Only as Follows:"
      },
      {
        "t": "ul",
        "items": [
          "With other party involved in the transaction as mentioned in section 6.",
          "With service providers who process data on our behalf under strict confidentiality and data protection obligations. Currently, we are using Supabase for database storing and authentication and file storage. We are using Netlify for website hosting, and Google for those users who log in using their Google accounts. We may add or remove providers in future, and this policy will be updated in case of any significant changes in terms of data handling.",
          "With authorities such as Kenyan law enforcement, courts, regulators, ODPC when required by law or when we have a good faith belief that it is necessary to disclose information to prevent or investigate any crime, fraud or some sort of harm or protect rights, property and safety of INUA, Users and the public.",
          "With respect to any business transfer which is the case in case of merger, acquisition, restructuring and sale of assets; recipient will comply with this policy and Users will be duly informed about it.",
          "If such information is provided by the User voluntarily."
        ]
      },
      {
        "t": "c",
        "title": "8.3 Aggregate Information.",
        "text": "We may utilize anonymized or aggregate information that cannot be tied to you for purposes of analyzing and enhancing the Platform."
      },
      {
        "t": "h",
        "text": "9. Liability for Third Parties and User Actions"
      },
      {
        "t": "c",
        "title": "9.1 Other Users.",
        "text": "INUA does not govern any acts of the other Users with the information they receive through the Platform."
      },
      {
        "t": "c",
        "title": "9.2 Communication Outside the Platform.",
        "text": "If you communicate through WhatsApp, by phone, email, social media or by any other means, the terms and privacy policies of that particular service shall apply, and INUA shall not bear the responsibility for them."
      },
      {
        "t": "c",
        "title": "9.3 Third-Party Services.",
        "text": "While we carefully chose our vendors, we are not responsible for independent privacy practices of third parties, including Google, or any other events happening with them beyond our reasonable influence."
      },
      {
        "t": "c",
        "title": "9.4 Illegal Use of Data.",
        "text": "You are not allowed to harvest, scrape, copy or compile any data from the Platform, attempt to access any accounts or data that do not belong to you, or access any data from the Platform for any purpose apart from that transaction. Any breach could cause immediate suspension of your account, legal action, or reporting to authorities."
      },
      {
        "t": "h",
        "text": "10. Your Data Storage Location (Cross-Border Data Transfer)"
      },
      {
        "t": "p",
        "text": "Your data might be located and processed on servers outside of Kenya operated by service providers (for example, Supabase, Netlify and Google). Whenever this occurs, we take reasonable measures to ensure that the transfer and retention of information are in accordance with the Act, including adequately approved measures or user consent whenever the law prescribes it. By accessing the Platform, you agree that such transfers may happen in the future."
      },
      {
        "t": "h",
        "text": "11. How Long We Keep Your Data"
      },
      {
        "t": "c",
        "title": "11.1 Basic Rule.",
        "text": "We will retain personal data only as long as necessary for the purposes stated in this policy or as required by law."
      },
      {
        "t": "c",
        "title": "11.2 Deleting Accounts.",
        "text": "When you delete your account or when we receive your request for deletion, we will proceed to delete or anonymise personal data of yours within 30 days after receiving your request except when we need to hold on to it to:"
      },
      {
        "t": "ul",
        "items": [
          "Meet any legal, regulatory or tax obligation.",
          "Prevent security and fraud, including keeping records on banned and suspended accounts in order to stop them from regaining entry in the most effective way.",
          "Deal with any dispute, complaint, investigation legal claims or directive from an authorised body.",
          "Make sure we are carrying out legal claims properly."
        ]
      },
      {
        "t": "c",
        "title": "11.3 Backups.",
        "text": "Backups' data may continue to exist for a certain amount of time even after being deleted, as it gets overwritten through the use of the regular procedure."
      },
      {
        "t": "c",
        "title": "11.4 Anonymised Data.",
        "text": "Anonymised data is not treated as personal data since it cannot be used to identify you."
      },
      {
        "t": "h",
        "text": "12. Your Rights"
      },
      {
        "t": "p",
        "text": "According to the law, you have the rights:"
      },
      {
        "t": "ul",
        "items": [
          "To receive details about the collection and usage of your personal data (the policy explains this).",
          "To check the details about your personal data.",
          "To amend any incorrect or insufficient information.",
          "To ask for the details of your personal data in a commonly used version or ask us to send it elsewhere, if that is technically possible (portability of data).",
          "To ask for the deletion of your data, subject to the limits in section 11.",
          "To object to or restrict certain methods of using your data.",
          "To revoke your consent. Revoking does not influence processing that occurred before revocation nor it limits your use of certain features of the Platform.",
          "To file a complaint with the Office of the Data Protection Commissioner."
        ]
      },
      {
        "t": "c",
        "title": "12.1 How to Exercise Your Rights.",
        "text": "You should send a request to trust@inuamarketplace.com from your registered email address. During this process, we may ask you to prove your identity, and in some cases, we may refuse or limit the request if it is unreasonable or futile, if the request gives away the information related to other people, or if it contradicts any of our legal obligations or our legitimate interests in protecting the Platform, as allowed by the Act. Our response will be sent within the term required by the Act."
      },
      {
        "t": "h",
        "text": "13. Data Breach Notification"
      },
      {
        "t": "p",
        "text": "In case a breach of personal data occurs which may possibly entail the actual risk of harm, INUA will inform the ODPC of the occurrence within 72 hours of learning about the incident if reasonably possible, as well as the affected individuals as soon as possible according to the requirements of the Act. We will make efforts to contain and rectify the breach."
      },
      {
        "t": "h",
        "text": "14. Children's Data"
      },
      {
        "t": "p",
        "text": "INUA is targeted at individuals who are 18 years and older. We don't intend on collecting personal data from individuals below the age of 18. If you are aged below 18, please refrain from registering or using our Platform. Should we discover that we possess data of a person who is under 18 years, we will erase this data and could potentially suspend or terminate the corresponding account. If you think that any information has been provided to us by a person under 18 years old, please reach out to us at trust@inuamarketplace.com."
      },
      {
        "t": "h",
        "text": "15. Security"
      },
      {
        "t": "p",
        "text": "We take appropriate technical and organizational measures to secure personal data from unauthorized access, loss, alteration and misuse. Data is sent through encrypted connections (HTTPS) and our service providers use encryption and security measures on stored data, while access to personal data is restricted to authorized personnel who need personal data in the course of performing their duties. Nevertheless, no system is infallible and we cannot assure you that your personal data will be completely secure."
      },
      {
        "t": "p",
        "text": "It is your obligation to choose complex credentials and keep them private and make sure that your devices are secure and that you log out from shared devices. To the fullest extent allowed by law, INUA will not be liable for unauthorized access due to your own negligence in protecting your credentials or devices, as well as due to users and third parties that are beyond our control."
      },
      {
        "t": "h",
        "text": "16. Limitation of Liability"
      },
      {
        "t": "p",
        "text": "INUA is not responsible for loss or damage (including but not limited to indirect, incidental, special or consequential loss) arising out of: (a) any information made public or shared by you or by other Users; (b) actions or omissions of other Users; (c) third-party service or other suppliers; (d) payments, pricing or agreements made by Users outside this Platform; or (e) events outside our reasonable control, to the extent permitted by law. Nothing in this policy will limit any liability that cannot legally be limited or restrict your rights according to any applicable act."
      },
      {
        "t": "h",
        "text": "17. Compliance, Enforcement and Account Actions"
      },
      {
        "t": "p",
        "text": "For the purpose of enforcing this policy and/or rules, the Company has the right to suspend, restrict, terminate or delete any account that is suspected of violating the terms of this policy and/or rules or suspected of any fraudulent activity."
      },
      {
        "t": "h",
        "text": "18. Changes to This Policy"
      },
      {
        "t": "p",
        "text": "They may change this policy from time to time whenever there is some change on the Platform, by the providers, or the law itself. The latest version will always be available on the Platform with its effective date. In cases of material changes, the Company will notify you about them through the Platform or through the email before their implementation. Where legislation requires obtaining a new consent, they will do so. By continuing to use the Platform after the changes' implementation, the user agrees to the updated policy. If the user does not agree, he has to stop using the Platform and delete the account."
      },
      {
        "t": "h",
        "text": "19. Governing Law and Disputes"
      },
      {
        "t": "p",
        "text": "This policy is subject to the law of the Republic of Kenya. Users may always contact the Company via trust@inuamarketplace.com as their first step toward resolving disputes. This does not cancel the fact that a user has the right to complain to the ODPC or to seek a judicial remedy in the competent Kenyan courts."
      },
      {
        "t": "h",
        "text": "20. General Provisions"
      },
      {
        "t": "c",
        "title": "20.1 Severability.",
        "text": "In case any portion of this policy is ruled insufficient or unable to be ratified, it shall be changed to the requisite level for it to be ratified or deleted if changes in the policy are not possible. The remaining provisions remain effective."
      },
      {
        "t": "c",
        "title": "20.2 Entire Agreement.",
        "text": "This document together with any terms of use and other policies and regulations incorporated by reference is the integral agreement between you and INUA in reference to treatment of the data."
      },
      {
        "t": "c",
        "title": "20.3 No Waiver.",
        "text": "If we do not enforce any provision, it does not mean that we cannot do so in the future."
      },
      {
        "t": "c",
        "title": "20.4 Assignment.",
        "text": "You are not entitled to assign or transfer your rights or obligations in accordance with the policy. INUA is entitled to assign this document regarding an event described in section 8."
      },
      {
        "t": "c",
        "title": "20.5 Language.",
        "text": "This policy is made in English. If it is translated, it will still be considered in the first place."
      },
      {
        "t": "h",
        "text": "21. Contact Us"
      },
      {
        "t": "p",
        "text": "If you have any questions regarding this Privacy Policy or your data, please contact us via email at trust@inuamarketplace.com."
      },
      {
        "t": "p",
        "text": "Kenya's Office of the Data Protection Commissioner: odpc.go.ke can be visited in case of complaints."
      },
      {
        "t": "p",
        "text": "© 2026 INUA. All rights reserved."
      }
    ]
  },
  "about": {
    "title": "About Us",
    "meta": null,
    "blocks": [
      {
        "t": "p",
        "text": "INUA is where Kenyan students and young entrepreneurs turn their skills into income and where anyone can discover them. The inception of INUA happened after observing something strange. Throughout the universities in Kenya, students and graduates have been running their own businesses such as braiding hair, crocheting bags, fixing laptops, baking cakes, and running errands for themselves between classes, but hardly anyone would be able to find them outside of WhatsApp."
      },
      {
        "t": "p",
        "text": "Visitors wanting to help a student or graduate's business rather than from anonymous online retailers who are far, do not have a simple way of locating trustworthy businesses."
      },
      {
        "t": "p",
        "text": "INUA solves this issue. Every seller featured on the platform is verified before their first listing is active; thus, every listing depicts real images and facts checked by the team instead of the algorithm. No stock known by a seller will be falsely advertised in their listing.  In case of any reports concerning listings, send an email to: trust@inuamarketplace.com, with the seller's display name and listing's name"
      }
    ]
  },
  "contact": {
    "title": "Contact Us",
    "meta": null,
    "blocks": [
      {
        "t": "p",
        "text": "We go through each and every message sent to us. Given below is the quickest way through which one can reach out to the respective person based on the situation."
      },
      {
        "t": "p",
        "text": "General inquiries and help [help@inuamarketplace.com] — we make it a priority to respond within 24 hours."
      },
      {
        "t": "p",
        "text": "Selling related concern and account issue [sellers@inuamarketplace.com]"
      },
      {
        "t": "p",
        "text": "Report any issue related to data safety, personal safety or complaint [trust@inuamarketplace.com] — send messages which are related to bad transactions, safety issues, and any act which made someone feel uncomfortable."
      },
      {
        "t": "p",
        "text": "Prefer WhatsApp? [+254 773816019] — message us directly."
      }
    ]
  },
  "faq": {
    "title": "Help / FAQ",
    "meta": null,
    "blocks": [
      {
        "t": "sub",
        "text": "For Buyers"
      },
      {
        "t": "faq",
        "q": "How can I tell if a seller is genuine?",
        "a": "All sellers registered with INUA have to complete an identity verification process before their listing is published. We verify their identity and view real photos of their work instead of stock pictures."
      },
      {
        "t": "faq",
        "q": "How does one make a payment to the seller?",
        "a": "Presently payment is done directly between the buyer and seller after they reach an agreement. The most common method is M-Pesa. We are working on improving our in-app payment security and will notify all users once that is done."
      },
      {
        "t": "faq",
        "q": "What happens if the seller fails to fulfil their promise?",
        "a": "It should be reported to us without a delay at trust@inuamarketplace.com. We take this matter seriously and would investigate it including correspondence between the buyer and seller if needed."
      },
      {
        "t": "faq",
        "q": "Is it okay to see the seller face to face?",
        "a": "When it comes to any personal transaction, use the safest locations. It is best to avoid secluded or private areas to meet a seller for the first time. Remember that INUA does not monitor face to face meetings at all."
      },
      {
        "t": "sub",
        "text": "For Sellers"
      },
      {
        "t": "faq",
        "q": "How long will it take to obtain verification?",
        "a": "Most items take between one and two days to be verified. You will receive an email informing you either way, whether you have been approved or whether you need to make some needed changes to your submission."
      },
      {
        "t": "faq",
        "q": "What kind of photos should be submitted?",
        "a": "Use only real images of your work which you took yourself in the last several weeks. Never submit photos of someone else's work or the images found online as doing so results in the permanent removal from INUA."
      },
      {
        "t": "faq",
        "q": "Can I sell more than one category?",
        "a": "At this moment you need to know that your seller account is limited to one category. This may change over time. However, within the seller's category, they are allowed to sell more than one sub-category, e.g Nails and Clothing both under Fashion & Beauty"
      },
      {
        "t": "faq",
        "q": "What should I do if a buyer does not show up or does not pay?",
        "a": "Report this to trust@inuamarketplace.com. If this happens repeatedly, the account of the buyer will be investigated."
      },
      {
        "t": "sub",
        "text": "Trust & Safety"
      },
      {
        "t": "faq",
        "q": "What actions does INUA take if a situation arises involving a buyer and seller?",
        "a": "We process all complaints submitted to us, and in certain circumstances, including when a situation is regarded as allegedly dangerous, carries threats of harassment, or requires an investigation of a case of an assault, INUA is entitled to check selected chats, which is done only in the case of receiving a complaint from a respective person."
      },
      {
        "t": "faq",
        "q": "Is there a guarantee that the goods offered for sale will be of an appropriate quality?",
        "a": "No. INUA does not take any part in the transaction and does not manufacture any goods. However, we check the identity of the seller and treat all claims about the intention to deceive sellers very seriously."
      }
    ]
  }
};

// Menu label -> document. Anything not listed here (e.g. "Home") isn't a page.
export const MENU_LABEL_TO_DOC: Record<string, LegalDocId> = {
  "Help / FAQ": "faq",
  "Contact Us": "contact",
  "About Us": "about",
  "Privacy Policy": "privacy",
  "Terms of Use": "terms",
};

// Menu items that open the person's email app instead of a page.
// "Report a Concern" goes straight to INUA's trust & safety address.
export const REPORT_EMAIL = "trust@inuamarketplace.com";
export const MENU_LABEL_TO_MAILTO: Record<string, string> = {
  "Report a Concern": `mailto:${REPORT_EMAIL}?subject=${encodeURIComponent(
    "INUA — Report a Concern"
  )}`,
};

// --- "I agree" record ---------------------------------------------------
// Acceptance is stored per ACCOUNT, per ROLE (buyer / seller) and per TERMS
// VERSION in the Supabase table `terms_acceptances` (see the SQL in the
// project notes). That table is the permanent, tamper-resistant record: users
// can add and read their own rows but never edit or delete them.
//
// A copy is also kept in this browser's localStorage purely as a speed-up and
// as a safety net — it means returning users aren't asked (or made to wait on
// a network call) again, and if the database can't be reached at the moment
// someone taps "I Agree", the acceptance is remembered locally and re-sent
// automatically the next time the app opens.
//
// Net effect: the sheet appears ONCE per account (per version), on the first
// device they use — not on every login, and not again on their other devices.

const localKey = (role: "buyer" | "seller", userId: string) =>
  // "v2": ignores any acceptance remembered by the earlier local-only build,
  // so everyone gets asked once more and properly recorded in the table.
  `inua_terms_accepted_v2_${role}_${userId}`;
const pendingKey = (role: "buyer" | "seller", userId: string) =>
  `inua_terms_pending_sync_${role}_${userId}`;

function readLocal(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLocal(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode etc. */
  }
}

// Writes the row. Safe to call twice: the unique (user, role, version) key
// makes a repeat a no-op instead of a duplicate.
async function insertAcceptance(
  role: "buyer" | "seller",
  userId: string
): Promise<boolean> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const { error } = await supabase.from("terms_acceptances").upsert(
      {
        user_id: userId,
        email: session?.user?.email ?? null,
        role,
        terms_version: TERMS_VERSION,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      },
      { onConflict: "user_id,role,terms_version", ignoreDuplicates: true }
    );
    if (error) {
      console.error("Could not record terms acceptance:", error.message);
      return false;
    }
    return true;
  } catch (err) {
    console.error("Could not record terms acceptance:", err);
    return false;
  }
}

// Has this account already accepted the CURRENT terms for this role?
export async function hasAcceptedTerms(
  role: "buyer" | "seller",
  userId: string
): Promise<boolean> {
  // 1. Quick path: accepted on this device already.
  if (readLocal(localKey(role, userId)) === TERMS_VERSION) {
    // Re-send an acceptance that couldn't be saved earlier.
    if (readLocal(pendingKey(role, userId))) {
      void insertAcceptance(role, userId).then((ok) => {
        if (ok) writeLocal(pendingKey(role, userId), null);
      });
    }
    return true;
  }

  // 2. Accepted on another device? Ask the database.
  try {
    const { data, error } = await supabase
      .from("terms_acceptances")
      .select("id")
      .eq("user_id", userId)
      .eq("role", role)
      .eq("terms_version", TERMS_VERSION)
      .limit(1);
    if (!error && data && data.length > 0) {
      writeLocal(localKey(role, userId), TERMS_VERSION);
      return true;
    }
  } catch {
    /* fall through: treat as not accepted yet */
  }
  return false;
}

// Called when they tap "I Agree". Remembers it on this device straight away
// (so the sheet closes instantly) and saves the permanent record.
export async function recordTermsAcceptance(
  role: "buyer" | "seller",
  userId: string
): Promise<void> {
  writeLocal(localKey(role, userId), TERMS_VERSION);
  const ok = await insertAcceptance(role, userId);
  if (!ok) writeLocal(pendingKey(role, userId), "1"); // retried next launch
}
